"use client";

import { c as _c } from "react/compiler-runtime";
import classNames from "classnames";
import React, { useMemo } from "react";
import WatchlistSvg from "src/components/svg/WatchlistSvg";
import WatchlistSvgWithGradient from "src/components/svg/WatchlistSvgWithGradient";
import { useUserContext } from "src/lib/contexts/ContextTypes";
import useWatchlist from "src/lib/filmwebid/useWatchlist";
import type { Movie, MovieClip, StreamingContent, WatchableContent } from "src/lib/movieinfo/movieinfotypes";
import type { IFilmomtale } from "src/lib/types/Filmomtale";
import type { ISearchResult } from "src/lib/types/search";
import styles from "./withWatchlist.module.css";
import { locale } from "@locale";
import type { IFilmwebIdGraphQLUserContent, IFilmwebIdMovieIdentifierType } from "src/lib/filmwebid/useMovieIdentifier";
import { getMovieIdentifier, matchesIdentifier, useMovieIdentifier } from "src/lib/filmwebid/useMovieIdentifier";
import { isStreamingContent } from "src/lib/movieinfo/typeGuards";
import getThemeStyles from "src/lib/styling/getThemeStyles";

//#region [Styles]
//#endregion

//#region [Props]
type DefaultOptionsType = {
  onComplete?: () => void;
  iconVersion?: "orig" | "gradient";
  preferType?: IFilmwebIdMovieIdentifierType;
};
type WithWatchlistProps = {
  wlcClassName?: string;
  wlcButtonClassName?: string;
  wlcStyle?: React.CSSProperties;
  wlcHideOnMobile?: boolean;
  wlcHideOnDesktop?: boolean;
};
//#endregion

/**
 * This HOC takes a component and adds watchlisting functionality as a button in the top-right corner.
 * The enhanced component will have some extra props added:
 * - wlcClassName: A classname for the withWatchlist container
 * - wlcButtonClassName: Any extra classes for the button
 * @param {Component} WrappedComponent The component to enhance
 * @param {string} watchableIdsParent The path to where movieId and/or streamingContentId can be found
 * @param {function} onComplete callback for when watchlist status toggles
 * @returns a new watchlistable component
 */
//#region [Component]
function withWatchlist<P, WatchableProp extends keyof P>(WrappedComponent: React.ComponentType<P>, watchableIdsParent: WatchableProp, options?: DefaultOptionsType) {
  const iconOption = options?.iconVersion ?? "orig";
  const themeStyles = getThemeStyles(styles);
  const EnhancedComponent = props => {
    const $ = _c(28);
    const userContext = useUserContext();
    const film = props[watchableIdsParent] as IFilmwebIdGraphQLUserContent | Movie | WatchableContent | IFilmomtale | MovieClip | ISearchResult | StreamingContent | null | undefined;
    const effectivePreferType = options?.preferType ?? (isStreamingContent(film) ? "StreamingId" : undefined);
    const movieIdentifier = useMovieIdentifier(film, effectivePreferType);
    let t0;
    if ($[0] !== movieIdentifier) {
      t0 = {
        movieIdentifier,
        onComplete: options?.onComplete
      };
      $[0] = movieIdentifier;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    const [watchlister, isWatchlisting] = useWatchlist(t0);
    let t1;
    bb0: {
      if (!movieIdentifier) {
        t1 = false;
        break bb0;
      }
      let t2;
      if ($[2] !== movieIdentifier || $[3] !== userContext.watchlistedList) {
        let t3;
        if ($[5] !== movieIdentifier) {
          t3 = item => item.movieIdentifiers?.some(id => matchesIdentifier(id, movieIdentifier));
          $[5] = movieIdentifier;
          $[6] = t3;
        } else {
          t3 = $[6];
        }
        t2 = userContext.watchlistedList.some(t3);
        $[2] = movieIdentifier;
        $[3] = userContext.watchlistedList;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      t1 = t2;
    }
    const isWatchlisted = t1;
    const t2 = props.wlcHideOnMobile === true;
    const t3 = props.wlcHideOnDesktop === true;
    let hideButton;
    let t4;
    if ($[7] !== props.wlcClassName || $[8] !== props.wlcHideOnDesktop || $[9] !== props.wlcHideOnMobile || $[10] !== t2 || $[11] !== t3) {
      const _styles = {
        ...themeStyles,
        [styles.SHideOnMobile]: t2,
        [styles.SHideOnDesktop]: t3
      };
      hideButton = props.wlcHideOnMobile === true && props.wlcHideOnDesktop === true;
      t4 = classNames(styles.SWithWatchList, props.wlcClassName, _styles);
      $[7] = props.wlcClassName;
      $[8] = props.wlcHideOnDesktop;
      $[9] = props.wlcHideOnMobile;
      $[10] = t2;
      $[11] = t3;
      $[12] = hideButton;
      $[13] = t4;
    } else {
      hideButton = $[12];
      t4 = $[13];
    }
    let t5;
    if ($[14] !== hideButton || $[15] !== isWatchlisted || $[16] !== isWatchlisting || $[17] !== movieIdentifier || $[18] !== props.wlcButtonClassName || $[19] !== watchlister) {
      t5 = !!movieIdentifier && !hideButton && <button disabled={isWatchlisting} className={classNames(styles.SWithWatchlistButton, props.wlcButtonClassName)} title={locale.watchlist.addToWatchlist} aria-label={locale.watchlist.addToWatchlist} onClick={() => watchlister(!isWatchlisted)}>{iconOption === "orig" && <WatchlistSvg className={styles.SWatchlistSvg} selected={isWatchlisted} />}{iconOption === "gradient" && <WatchlistSvgWithGradient className={styles.SWatchlistSvg_gradient} selected={isWatchlisted} />}</button>;
      $[14] = hideButton;
      $[15] = isWatchlisted;
      $[16] = isWatchlisting;
      $[17] = movieIdentifier;
      $[18] = props.wlcButtonClassName;
      $[19] = watchlister;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    let t6;
    if ($[21] !== props) {
      t6 = <WrappedComponent {...props} />;
      $[21] = props;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    let t7;
    if ($[23] !== props.wlcStyle || $[24] !== t4 || $[25] !== t5 || $[26] !== t6) {
      t7 = <div className={t4} style={props.wlcStyle}>{t5}{t6}</div>;
      $[23] = props.wlcStyle;
      $[24] = t4;
      $[25] = t5;
      $[26] = t6;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    return t7;
  };
  EnhancedComponent.displayName = `WithWatchlist(${getDisplayName(WrappedComponent)})`;
  return EnhancedComponent;
}
;
export default withWatchlist;
//#endregion

//#region [Other]
function getDisplayName<P>(WrappedComponent: React.ComponentType<P>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}
//#endregion